/**
 * Cold-start timings on the installed app (2026-10-02).
 *
 * LeagueHub's first iOS build sat on empty screens for 10-15 seconds because
 * WKWebView buffers the WebChannel stream the Firestore SDK reads on (house
 * doc docs/house-ios-deployment.md §6, "Firestore transport"). Vekkle now
 * forces long polling on native; this records whether launches are still slow.
 *
 * When a launch is slow, ONE `native.boot` row goes to `clientErrors` (codes
 * and numbers only, never content): how long the first App Check token took
 * on native (reCAPTCHA Enterprise cannot attest there yet, so it may fail or
 * hang), and how long the launch's first Firestore read - the Garage sync -
 * took, with whether the device cache answered it.
 *
 * Pure apart from the shared stats object: the caller sends the row and marks
 * the launch reported. Same file in both Vekkle apps; LeagueHub's
 * core/utils/native-boot.ts is the original. The message is a constant so
 * rows group together in clientErrors; the numbers live in `extra`.
 */

export interface NativeBootStats {
  /** How long the first native App Check token took, or null until one arrives. */
  appCheckFirstTokenMs: number | null;
  /** The code of the first native App Check failure, if the first fetch failed. */
  appCheckFirstTokenError: string | null;
  /** This launch has sent its row. */
  reported: boolean;
}

/** The one record for this launch. */
export const nativeBootStats: NativeBootStats = {
  appCheckFirstTokenMs: null,
  appCheckFirstTokenError: null,
  reported: false,
};

/** A first App Check token slower than this is worth a row: a cached one takes milliseconds. */
export const SLOW_APP_CHECK_MS = 2_000;
/** A first read slower than this is worth a row: a connected read takes well under a second. */
export const SLOW_FIRST_READ_MS = 3_000;

/** Which Firestore transport this build uses on native, so rows from before and after compare. */
export const NATIVE_TRANSPORT = 'long-polling';

export interface NativeBootInput {
  native: boolean;
  stats: NativeBootStats;
  /** The launch's first Firestore read, or null when it never completed. */
  firstReadMs: number | null;
  /** Whether that read was answered from the device cache. */
  fromCache: boolean | null;
  /** Documents the read returned (-1 when it failed). */
  docs: number;
  /** Milliseconds since the page started when the read finished. */
  sinceLaunchMs: number;
  /** The read's error code, when it failed. */
  readError?: string;
}

export interface NativeBootReport {
  message: string;
  extra: Record<string, number | string | boolean>;
}

/** The row to send for this launch, or null: not the installed app, already sent, or nothing was slow. */
export function nativeBootReport(input: NativeBootInput): NativeBootReport | null {
  if (!input.native || input.stats.reported) return null;
  const appCheckMs = input.stats.appCheckFirstTokenMs;
  const slowAppCheck = appCheckMs !== null && appCheckMs >= SLOW_APP_CHECK_MS;
  const slowRead = input.firstReadMs !== null && input.firstReadMs >= SLOW_FIRST_READ_MS;
  const failed = !!input.stats.appCheckFirstTokenError || !!input.readError;
  if (!slowAppCheck && !slowRead && !failed) return null;
  return {
    message: 'native.boot',
    extra: {
      appCheckMs: appCheckMs ?? -1,
      appCheckError: input.stats.appCheckFirstTokenError ?? '',
      firstReadMs: input.firstReadMs ?? -1,
      fromCache: input.fromCache ?? false,
      readError: input.readError ?? '',
      docs: input.docs,
      sinceLaunchMs: input.sinceLaunchMs,
      transport: NATIVE_TRANSPORT,
    },
  };
}

/** Time the first App Check token on native into nativeBootStats. Never throws. */
export function timeFirstAppCheckToken(getToken: () => Promise<unknown>): void {
  const started = performance.now();
  getToken().then(
    () => {
      if (nativeBootStats.appCheckFirstTokenMs === null) nativeBootStats.appCheckFirstTokenMs = Math.round(performance.now() - started);
    },
    (e: { code?: string; message?: string } | undefined) => {
      if (nativeBootStats.appCheckFirstTokenMs === null && !nativeBootStats.appCheckFirstTokenError) {
        nativeBootStats.appCheckFirstTokenError = String(e?.code ?? e?.message ?? 'unknown').slice(0, 120);
      }
    },
  );
}
